import {ColorPicker} from '@wix/wix-ui-tpa/cssVars';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {IProductOptionSelection} from '@wix/wixstores-graphql-schema';
import classNames from 'classnames';
import React, {ComponentProps} from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {RenderContext, withRenderContext, type WithRenderContextProps} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {IProductGroupMember} from '../../../types/app-types';
import {buildSelectionAriaLabel} from '../utils/selectionAriaLabel';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import {st, classes as colorOptionStyles} from './ColorOption.st.css';
import s from './ProductColor.scss';

/* istanbul ignore next */
const getSwatchImageProps = (
  displayImage: {url?: string | null; width?: number | null; height?: number | null} | null | undefined
) => {
  return {
    imageSrc: displayImage?.url ?? undefined,
    imageSourceWidth: displayImage?.width ?? undefined,
    imageSourceHeight: displayImage?.height ?? undefined,
  };
};

export interface ProductColorsProps extends ProvidedGlobalProps, IProvidedTranslationProps, WithRenderContextProps {
  optionIndex: number;
  isRequired: boolean;
  colorPickerCounter?: number;
  id?: string;
  showErrors?: boolean;
  showTooltips?: boolean;
}

export enum ProductColorsDataHook {
  Container = 'product-options-color-container',
  ColorOption = 'product-options-color',
  ColorOptionItem = 'product-options-color-item',
  ColorOptionTitle = 'product-colors-title',
}

class ProductColorsComponent extends React.Component<ProductColorsProps> {
  private readonly colorPickerContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: ProductColorsProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.colorPickerContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.colorPickerContainerRef.current, isQuickView);
      }
    }
  }

  private readonly handleChange: ComponentProps<typeof ColorPicker>['onChange'] = (event, itemId) => {
    const {
      optionIndex,
      renderContext,
      globals: {handleUserInput, validateUserInputsChanges, userInputs},
    } = this.props;

    const isStickyDrawer = renderContext === RenderContext.StickyDrawer;

    const nextSelection = this.productOption.selections.find((selection) => selection.description === itemId);

    const isDeselected =
      nextSelection && userInputs[UserInputType.Selection].some((selection) => selection?.id === nextSelection.id);

    handleUserInput(UserInputType.Selection, isDeselected ? undefined : nextSelection, optionIndex, isStickyDrawer);

    validateUserInputsChanges();
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;

    return userInputs[UserInputType.Selection][optionIndex] ?? undefined;
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get isGroupedByOption(): boolean {
    const {productGroup, isCombinedListing} = this.props.globals;
    return !!isCombinedListing && this.productOption.id === productGroup?.groupingCustomizationId;
  }

  private get isShowHiddenVariantsEnabled(): boolean {
    return this.props.globals.experiments.showHiddenVariants;
  }

  private get optionAvailability(): IProductSelectionAvailabilityMap[string] {
    const {variantInfo} = this.props.globals;
    return variantInfo.selectionsAvailability[this.productOption.id];
  }

  private get visibleSelections(): IProductOptionSelection[] {
    return this.productOption.selections.filter(
      (selection) => this.isShowHiddenVariantsEnabled || this.optionAvailability[selection.id].isVisible
    );
  }

  private readonly renderGroupedColorOption = (member: IProductGroupMember, index: number) => {
    const {
      showTooltips = true,
      globals: {product, groupedMemberUrls, isEditorMode},
    } = this.props;
    const href = !isEditorMode ? groupedMemberUrls?.[member.slug] : undefined;

    return (
      <a key={index} href={href} className={s.groupedSwatchLink}>
        <ColorPicker.Item
          id={member.choice.description}
          aria-label={member.choice.description}
          value={member.choice.value}
          isCrossedOut={member.inventoryAvailabilityStatus === 'OUT_OF_STOCK'}
          tooltip={showTooltips ? member.choice?.description : undefined}
          checked={member.productId === product.id}
          data-hook={ProductColorsDataHook.ColorOptionItem}
          className={s.colorOptions}
        />
      </a>
    );
  };

  private readonly renderColorOption = (selection: IProductOptionSelection) => {
    const {
      t,
      showTooltips = true,
      globals: {isBackInStockEnabled},
    } = this.props;
    const {isVisible, isSelectable: isInStock} = this.optionAvailability[selection.id];
    const isOutOfStock = !isInStock;
    const isHidden = this.isShowHiddenVariantsEnabled && !isVisible;

    return (
      <ColorPicker.Item
        key={selection.id}
        id={selection.description}
        aria-label={buildSelectionAriaLabel(
          selection.description,
          this.isShowHiddenVariantsEnabled && (isOutOfStock || isHidden),
          t('productPage.unavailable.button')
        )}
        value={selection.value}
        {...getSwatchImageProps(selection.displayImage)}
        disabled={this.isShowHiddenVariantsEnabled ? false : !isBackInStockEnabled && isOutOfStock}
        isCrossedOut={isOutOfStock || isHidden}
        tooltip={showTooltips ? selection.description : undefined}
        checked={(this.activeSelection?.id ? [this.activeSelection.id] : []).includes(selection.id)}
        data-hook={ProductColorsDataHook.ColorOptionItem}
        className={s.colorOptions}
      />
    );
  };

  private get colorPickerOptions(): JSX.Element[] {
    const {productGroup} = this.props.globals;

    return this.isGroupedByOption
      ? productGroup?.members?.map(this.renderGroupedColorOption)
      : this.visibleSelections.map(this.renderColorOption);
  }

  public readonly renderDescription = () => (this.activeSelection ? `: ${this.activeSelection.description}` : '');

  private get groupedDescription(): string {
    const {
      globals: {productGroup, product},
    } = this.props;
    const currentMember = productGroup?.members?.find((m) => m.productId === product.id);
    return currentMember?.choice?.description ? `: ${currentMember.choice.description}` : '';
  }

  private renderColorPicker() {
    const {
      optionIndex,
      isRequired,
      showErrors = true,
      globals: {isProductSubmitted, userInputErrors, isQuickView, useExperiments},
      t,
      renderContext,
    } = this.props;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const shouldShowErrors = showErrors || !isStickyAddToCartEnabled;
    const hasError = isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex];
    const errorMessage =
      shouldShowErrors && hasError ? t('SELECT_OPTION_WARNING', {optionTitle: this.productOption.title}) : undefined;

    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;
    const title = this.productOption.title;
    const optionalLabel = `${title} (${t('OPTIONAL')})`;
    const label = `${isRequired ? title : optionalLabel}${
      this.isGroupedByOption ? this.groupedDescription : this.renderDescription()
    }`;

    return (
      <ColorPicker
        className={classNames(
          st(colorOptionStyles.colorOption, {stickyDrawer: isStickyDrawer}),
          {[s.productOptionsColorContainer]: isQuickView},
          {[s.title]: true}
        )}
        label={label}
        onChange={this.handleChange}
        data-hook={ProductColorsDataHook.ColorOption}
        errorMessage={errorMessage}
        aria-required={isRequired}
        required={isRequired}
        showAsterisk={isRequired}>
        {this.colorPickerOptions}
      </ColorPicker>
    );
  }

  public render(): JSX.Element {
    const {colorPickerCounter} = this.props;
    const {useExperiments} = this.props.globals;
    const id = useExperiments.enabled(SPECS.StoresStickyAddToCart)
      ? this.props.id
      : `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${colorPickerCounter}`;

    const selectionIds = this.productOption.selections.map((selection) => selection.id).join('_');
    const titleId = `product-colors-title-${selectionIds}`;

    return (
      <div
        id={id}
        data-hook={ProductColorsDataHook.Container}
        aria-labelledby={titleId}
        className={s.uiTpaColoPicker}
        ref={this.colorPickerContainerRef}>
        <div className={s.actionable}>{this.renderColorPicker()}</div>
      </div>
    );
  }
}

export const ProductColors = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(ProductColorsComponent))
);
